import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Box, Grid, Typography } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"

import { useEditableFeaturesQuery, useUpdateEditableFeaturesMutation } from "features/api"
import { isFeatureOff } from "helpers/errors/errorTypes"
import usePermissions from "helpers/hooks/usePermissions"
import {
  isValidLocaleNumber,
  localeNumberToStandard,
  numberToLocale,
} from "helpers/utils/localeNumber"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import CardWithTitle from "widgets/common/CardWithTitle"
import ConfirmationPopup from "widgets/common/ConfirmationPopup"
import ControlledInput from "widgets/common/ControlledInput"
import FormButtons from "widgets/common/FormButtons"
import LabelValueStack from "widgets/common/LabelValueStack"
import SkeletonWrapper from "widgets/common/SkeletonWrapper"

const EMISSIVITY_DECIMALS = 4

const roundEmissivity = (value: number) => Number(value.toFixed(EMISSIVITY_DECIMALS))

interface MeasurementParametersForm {
  emissivity: string
}

interface MeasurementParametersProps {
  lessId: number
}

const MeasurementParameters = ({ lessId }: MeasurementParametersProps) => {
  const { t, i18n } = useTranslation()
  const { isAdmin } = usePermissions()
  const locale = i18n.resolvedLanguage || "en"

  const [isEditing, setIsEditing] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const { currentData, isFetching, error } = useEditableFeaturesQuery({ lessId })
  const [updateEditableFeatures, { isLoading: isSubmitting }] =
    useUpdateEditableFeaturesMutation()

  const methods = useForm<MeasurementParametersForm>({
    mode: "all",
    defaultValues: { emissivity: "" },
  })

  const rules = useMemo(() => {
    const toNumber = (value: string) =>
      isValidLocaleNumber(value, locale) ? localeNumberToStandard(value, locale) : null

    return {
      required: t("generic.FIELD_REQUIRED"),
      validate: {
        range: (value: string) => {
          const number = toNumber(value)
          return (
            number === null ||
            (number > 0 && number <= 1) ||
            t("device_settings.EMISSIVITY_RANGE")
          )
        },
        decimals: (value: string) => {
          const number = toNumber(value)
          return (
            number === null ||
            roundEmissivity(number) === number ||
            t("device_settings.EMISSIVITY_MAX_DECIMALS", {
              decimals: EMISSIVITY_DECIMALS,
            })
          )
        },
      },
    }
  }, [locale, t])

  const displayValue = useMemo(() => {
    const emissivity = currentData?.emissivity
    if (emissivity === null || emissivity === undefined) return "-"
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: EMISSIVITY_DECIMALS,
    }).format(emissivity)
  }, [currentData?.emissivity, locale])

  // Only the first value needs the warning, there is no way back to empty after it.
  const confirmDescription = useMemo(() => {
    const description = t("device_settings.ARE_YOU_SURE_TO_UPDATE_EMISSIVITY")
    return currentData?.emissivity === null
      ? [description, t("device_settings.EMISSIVITY_CANNOT_BE_EMPTIED")]
      : description
  }, [currentData?.emissivity, t])

  const handleEdit = useCallback(() => {
    const emissivity = currentData?.emissivity
    // The API takes any precision, but the form accepts the decimals the view shows.
    const rounded =
      typeof emissivity === "number" ? roundEmissivity(emissivity) : emissivity
    methods.reset({ emissivity: numberToLocale(rounded, locale) ?? "" })
    setIsEditing(true)
  }, [currentData?.emissivity, locale, methods])

  const handleClose = useCallback(() => {
    setIsEditing(false)
  }, [])

  const confirmUpdate = useCallback(() => {
    const emissivity = localeNumberToStandard(methods.getValues("emissivity"), locale)
    if (emissivity === null) return

    const item = t("conductors.EMISSIVITY")
    const getErrorMessage = buildGetErrorMessage(t("error.UPDATING_ITEM", { item }))

    snackbarMutation({
      mutation: updateEditableFeatures({ lessId, emissivity }).unwrap(),
      getErrorMessage: (error) =>
        isFeatureOff(error) ? undefined : getErrorMessage(error),
      getSuccessMessage: () =>
        t("success.UPDATING_ITEM", { item, count: 1, context: "female" }),
    })
      .then(() => setIsEditing(false))
      .catch(() => {})
      .finally(() => setIsConfirmOpen(false))
  }, [lessId, locale, methods, t, updateEditableFeatures])

  if (isFeatureOff(error)) return null

  return (
    <>
      <CardWithTitle
        titleKey="device_settings.MEASUREMENT_PARAMETERS"
        actionPermission={isAdmin && !!currentData}
        isDoingAction={isEditing}
        handleAction={handleEdit}
        actionIcon={<EditIcon fontSize="small" />}
      >
        <form onSubmit={methods.handleSubmit(() => setIsConfirmOpen(true))}>
          <FormProvider {...methods}>
            <Grid container>
              <Grid item xs={12} md={6}>
                <LabelValueStack
                  labelKey="conductors.EMISSIVITY"
                  tooltipContent={[t("device_settings.EMISSIVITY_TOOLTIP")]}
                  value={
                    isEditing ? (
                      <ControlledInput
                        styles={{ mb: 0 }}
                        name="emissivity"
                        type="number"
                        variant="outlined"
                        size="small"
                        placeholder=""
                        aria-label={t("conductors.EMISSIVITY")}
                        rules={rules}
                        disabled={isSubmitting}
                      />
                    ) : (
                      <SkeletonWrapper isWaiting={isFetching} width={150}>
                        <Typography color="text.disabled">{displayValue}</Typography>
                      </SkeletonWrapper>
                    )
                  }
                />
              </Grid>
            </Grid>
            {isEditing && (
              <Box mt={2}>
                <FormButtons
                  onClose={handleClose}
                  buttonText="generic.UPDATE"
                  isLoading={isSubmitting}
                  disabled={!methods.formState.isDirty}
                />
              </Box>
            )}
          </FormProvider>
        </form>
      </CardWithTitle>
      <ConfirmationPopup
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        description={confirmDescription}
        mainButtonText={t("generic.CONFIRM")}
        onConfirm={confirmUpdate}
        isLoading={isSubmitting}
      />
    </>
  )
}

export default MeasurementParameters
